/**
 * Ensures a font is loaded and available for Canvas2D rendering
 * before PIXI.Text tries to use it.
 *
 * Browsers lazy-load fonts — if Canvas2D renders text with a font
 * that hasn't been activated yet, it silently falls back to the
 * default font, and PIXI caches that wrong rendering.
 *
 * @param {string} fontFamily - The font family name to preload
 * @param {number} [fontSize=36] - Font size hint for the load request
 * @param {number} [timeoutMs=3000] - Max time to wait before giving up
 */
import { GOOGLE_FONTS, ensureGoogleFontsStylesheet } from './googleFonts';

export const ensureFontLoaded = async (fontFamily, fontSize = 36, timeoutMs = 3000) => {
  // Skip for default/safe system fonts that are always available
  if (!fontFamily || fontFamily === 'Arial' || fontFamily === 'sans-serif') return;

  try {
    if (GOOGLE_FONTS.includes(fontFamily)) {
      await ensureGoogleFontsStylesheet();
    }
    await Promise.race([
      document.fonts.load(`${fontSize}px "${fontFamily}"`),
      new Promise(resolve => setTimeout(resolve, timeoutMs))
    ]);
    if (document.fonts && document.fonts.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise(resolve => setTimeout(resolve, timeoutMs))
      ]);
    }
  } catch {
    // Font load failed — PIXI will use fallback, which is acceptable
    console.warn(`Font preload failed for "${fontFamily}", will use fallback`);
  }
};
